/**
 * TransactionFieldsSettings Page
 *
 * Configure workspace-level custom fields for transactions (e.g. invoice number,
 * receipt attachment). Shows "For: …" and "In use: …" per type; fields in use
 * can only be hidden, not deleted.
 */

import {
  ReactElement,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  sortableKeyboardCoordinates,
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import {
  Box,
  Button,
  Typography,
  Paper,
  Stack,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Tooltip,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import { Timestamp } from "firebase/firestore";
import type {
  TransactionCustomField,
  TransactionCustomFieldType,
  TransactionCustomFieldScopeType,
  TransactionCustomFieldIconName,
} from "../../types";
import { useTransactionCustomFieldsSettings } from "./hooks/useTransactionCustomFieldsSettings";
import { useTransactionFieldUsage } from "./hooks/useTransactionFieldUsage";
import { TransactionFieldDialog } from "./components/TransactionFieldDialog";
import TransactionFieldTable from "./components/TransactionFieldTable";
import { TransactionFieldIcon } from "./components/TransactionFieldIcon";
import { HiddenTransactionFieldRow, TRANSACTION_FIELD_TABLE_CELL_SX } from "./components/SortableTransactionFieldRow";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";
import LoadingSpinner from "../../components/LoadingSpinner";
import { isDemoMode } from "../../utils/demoMode";

interface TransactionFieldsSettingsProps {
  workspaceId: string;
  /** When true, all edit actions (add, edit, hide, delete) are disabled. Used when user lacks edit preferences permission. */
  readOnly?: boolean;
}

const TRANSACTION_TYPES: TransactionCustomFieldScopeType[] = ["expense", "transfer", "income"];

/** Tab order for UI: Expense, Transfer, Income (matches create-transaction mobile tabs). */
const TRANSACTION_TYPE_TABS: { value: TransactionCustomFieldScopeType; label: string }[] = [
  { value: "expense", label: "Expenses" },
  { value: "transfer", label: "Transfer" },
  { value: "income", label: "Income" },
];

/** Accent color per type (like Category: expense=red, income=green). Transfer=blue. */
function getAccentColorForType(type: TransactionCustomFieldScopeType): "error" | "success" | "info" {
  if (type === "expense") return "error";
  if (type === "income") return "success";
  return "info";
}

/** Resolve which transaction type a field belongs to (supports legacy transactionTypes). */
function getFieldTransactionType(field: TransactionCustomField): TransactionCustomFieldScopeType {
  return field.transactionType ?? field.transactionTypes?.[0] ?? "expense";
}

function isFieldInUse(
  fieldId: string,
  fieldUsageByType: Record<string, { expense: number; income: number; transfer: number }>
): boolean {
  const u = fieldUsageByType[fieldId];
  if (!u) return false;
  return u.expense > 0 || u.income > 0 || u.transfer > 0;
}

export default function TransactionFieldsSettings({
  workspaceId,
  readOnly = false,
}: TransactionFieldsSettingsProps): ReactElement {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDraggingField, setIsDraggingField] = useState(false);

  const {
    fields,
    loading,
    error,
    setFields,
    saveFields,
  } = useTransactionCustomFieldsSettings({ workspaceId });
  const { fieldUsageByType, loading: usageLoading } =
    useTransactionFieldUsage({ workspaceId });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState<TransactionCustomField | null>(null);
  const [addForTransactionType, setAddForTransactionType] = useState<TransactionCustomFieldScopeType | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState<TransactionCustomField | null>(null);
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<TransactionCustomFieldScopeType>("expense");
  const [hiddenAccordionExpandedByType, setHiddenAccordionExpandedByType] = useState<Record<TransactionCustomFieldScopeType, boolean>>({
    expense: false,
    income: false,
    transfer: false,
  });
  const hiddenAccordionRefs = useRef<Record<TransactionCustomFieldScopeType, HTMLDivElement | null>>({
    expense: null,
    income: null,
    transfer: null,
  });

  // Auto-scroll when dragging near top/bottom on mobile (match CurrencySettings)
  useEffect(() => {
    if (!isMobile || !isDraggingField) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const EDGE_ZONE = 0.25;
    const SCROLL_STEP = 12;
    const handlePointerMove = (e: PointerEvent) => {
      const rect = container.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const relativeY = y / rect.height;
      if (relativeY < EDGE_ZONE) container.scrollTop -= SCROLL_STEP;
      else if (relativeY > 1 - EDGE_ZONE) container.scrollTop += SCROLL_STEP;
    };
    document.addEventListener("pointermove", handlePointerMove, { passive: true });
    return () => document.removeEventListener("pointermove", handlePointerMove);
  }, [isMobile, isDraggingField]);

  const handleAdd = useCallback((forTransactionType: TransactionCustomFieldScopeType) => {
    setEditingField(null);
    setAddForTransactionType(forTransactionType);
    setDialogOpen(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setEditingField(null);
    setAddForTransactionType(null);
  }, []);

  const handleEdit = useCallback((field: TransactionCustomField) => {
    setEditingField(field);
    setDialogOpen(true);
  }, []);

  const handleDialogConfirm = useCallback(
    async (payload: {
      label: string;
      type: TransactionCustomFieldType;
      transactionType: TransactionCustomFieldScopeType;
      icon?: TransactionCustomFieldIconName;
    }) => {
      if (isDemoMode()) return;
      setSaving(true);
      try {
        if (editingField) {
          const next = fields.map((f) =>
            f.id === editingField.id
              ? {
                  ...f,
                  label: payload.label,
                  type: payload.type,
                  transactionType: payload.transactionType,
                  transactionTypes: undefined as TransactionCustomFieldScopeType[] | undefined,
                  icon: payload.icon,
                }
              : f
          );
          await saveFields(next);
        } else {
          const typeFields = fields.filter((f) => getFieldTransactionType(f) === payload.transactionType);
          const nextOrder = typeFields.length === 0 ? 0 : Math.max(...typeFields.map((f) => f.order), -1) + 1;
          const newField: TransactionCustomField = {
            id: `field_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
            label: payload.label,
            type: payload.type,
            order: nextOrder,
            transactionType: payload.transactionType,
            icon: payload.icon,
            createdAt: Timestamp.now(),
          };
          await saveFields([...fields, newField]);
        }
        handleDialogClose();
      } finally {
        setSaving(false);
      }
    },
    [editingField, fields, saveFields, handleDialogClose]
  );

  const handleHide = useCallback(
    async (field: TransactionCustomField) => {
      if (isDemoMode() || field.hidden) return;
      const type = getFieldTransactionType(field);
      const next = fields.map((f) =>
        f.id === field.id ? { ...f, hidden: true } : f
      );
      const prev = fields;
      setFields(next);
      requestAnimationFrame(() => {
        setHiddenAccordionExpandedByType((s) => ({ ...s, [type]: true }));
        requestAnimationFrame(() => {
          hiddenAccordionRefs.current[type]?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        });
      });
      try {
        await saveFields(next);
      } catch (e) {
        setFields(prev);
        console.error("Failed to hide field", e);
      }
    },
    [fields, setFields, saveFields]
  );

  const handleUnhide = useCallback(
    async (field: TransactionCustomField) => {
      if (isDemoMode() || !field.hidden) return;
      const next = fields.map((f) =>
        f.id === field.id ? { ...f, hidden: false } : f
      );
      const prev = fields;
      setFields(next);
      try {
        await saveFields(next);
      } catch (e) {
        setFields(prev);
        console.error("Failed to unhide field", e);
      }
    },
    [fields, setFields, saveFields]
  );

  const handleDeleteClick = useCallback((field: TransactionCustomField) => {
    setFieldToDelete(field);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!fieldToDelete || isDemoMode()) {
      setDeleteDialogOpen(false);
      setFieldToDelete(null);
      return;
    }
    setSaving(true);
    try {
      const next = fields
        .filter((f) => f.id !== fieldToDelete.id)
        .map((f, i) => ({ ...f, order: i }));
      await saveFields(next);
      setDeleteDialogOpen(false);
      setFieldToDelete(null);
    } finally {
      setSaving(false);
    }
  }, [fieldToDelete, fields, saveFields]);

  const inUseSet = useMemo(() => {
    const set = new Set<string>();
    Object.keys(fieldUsageByType).forEach((id) => {
      if (isFieldInUse(id, fieldUsageByType)) set.add(id);
    });
    return set;
  }, [fieldUsageByType]);

  /** Visible and hidden fields grouped by transaction type (each type has its own list and ordering). */
  const visibleByType = useMemo(() => {
    const byType: Record<TransactionCustomFieldScopeType, TransactionCustomField[]> = {
      expense: [],
      income: [],
      transfer: [],
    };
    fields.filter((f) => !f.hidden).forEach((f) => {
      const t = getFieldTransactionType(f);
      byType[t].push(f);
    });
    TRANSACTION_TYPES.forEach((t) => {
      byType[t].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    });
    return byType;
  }, [fields]);

  const hiddenByType = useMemo(() => {
    const byType: Record<TransactionCustomFieldScopeType, TransactionCustomField[]> = {
      expense: [],
      income: [],
      transfer: [],
    };
    fields.filter((f) => f.hidden).forEach((f) => {
      const t = getFieldTransactionType(f);
      byType[t].push(f);
    });
    TRANSACTION_TYPES.forEach((t) => {
      byType[t].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    });
    return byType;
  }, [fields]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleFieldDragStart = useCallback((event: DragStartEvent) => {
    setActiveFieldId(String(event.active.id));
    if (isMobile) setIsDraggingField(true);
  }, [isMobile]);

  const handleFieldDragEnd = useCallback(
    async (event: DragEndEvent) => {
      if (isMobile) setIsDraggingField(false);
      const { active, over } = event;
      const noOp = !over || active.id === over.id;
      if (noOp) {
        setActiveFieldId(null);
        return;
      }
      if (isDemoMode()) {
        setActiveFieldId(null);
        return;
      }
      const activeField = fields.find((f) => f.id === active.id);
      const overField = fields.find((f) => f.id === over.id);
      if (!activeField || !overField) {
        setActiveFieldId(null);
        return;
      }
      const dragType = getFieldTransactionType(activeField);
      const overType = getFieldTransactionType(overField);
      if (dragType !== overType) {
        setActiveFieldId(null);
        return;
      }
      const visibleOfType = visibleByType[dragType];
      const hiddenOfType = hiddenByType[dragType];
      const oldIndex = visibleOfType.findIndex((f) => f.id === active.id);
      const newIndex = visibleOfType.findIndex((f) => f.id === over.id);
      if (oldIndex === -1 || newIndex === -1) {
        setActiveFieldId(null);
        return;
      }
      const reorderedVisibleOfType = arrayMove(visibleOfType, oldIndex, newIndex);
      const otherTypes = TRANSACTION_TYPES.filter((t) => t !== dragType);
      const otherVisible = otherTypes.flatMap((t) => visibleByType[t]);
      const otherHidden = otherTypes.flatMap((t) => hiddenByType[t]);
      const reorderedWithOrder = reorderedVisibleOfType.map((f, i) => ({ ...f, order: i }));
      const hiddenWithOrder = hiddenOfType.map((f, i) => ({ ...f, order: reorderedVisibleOfType.length + i }));
      const withOrder: TransactionCustomField[] = [
        ...reorderedWithOrder,
        ...hiddenWithOrder,
        ...otherVisible,
        ...otherHidden,
      ];
      setSaving(true);
      try {
        await saveFields(withOrder);
      } finally {
        setSaving(false);
        setActiveFieldId(null);
      }
    },
    [isMobile, fields, visibleByType, hiddenByType, saveFields]
  );

  const handleFieldDragCancel = useCallback(() => {
    setActiveFieldId(null);
    if (isMobile) setIsDraggingField(false);
  }, [isMobile]);

  /** Visible fields for the currently selected tab only (for list + drag overlay). */
  const visibleForSelectedTab = useMemo(
    () => visibleByType[selectedTab],
    [visibleByType, selectedTab]
  );
  const activeField = activeFieldId ? visibleForSelectedTab.find((f) => f.id === activeFieldId) : null;
  const canAdd = !readOnly && !isDemoMode() && !(saving && (dialogOpen || deleteDialogOpen));

  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, height: "100%", justifyContent: "center", alignItems: "center" }}>
        <LoadingSpinner />
      </Box>
    );
  }

  const accentColor = getAccentColorForType(selectedTab);
  const accentColorMain = accentColor === "error" ? "error.main" : accentColor === "success" ? "success.main" : "info.main";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, height: "100%" }}>
      <Tabs
        value={TRANSACTION_TYPE_TABS.findIndex((t) => t.value === selectedTab)}
        onChange={(_, newValue) => setSelectedTab(TRANSACTION_TYPE_TABS[newValue].value)}
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          minHeight: isMobile ? 40 : 48,
          flexShrink: 0,
          "& .MuiTab-root": { minHeight: isMobile ? 40 : 48, py: isMobile ? 1 : 1.5 },
          "& .MuiTab-root.Mui-selected": { color: accentColorMain },
          "& .MuiTabs-indicator": { backgroundColor: accentColorMain },
        }}
      >
        {TRANSACTION_TYPE_TABS.map((t) => (
          <Tab key={t.value} label={t.label} />
        ))}
      </Tabs>

      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          p: 0,
          WebkitOverflowScrolling: "touch",
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleFieldDragStart}
          onDragEnd={handleFieldDragEnd}
          onDragCancel={handleFieldDragCancel}
        >
          <SortableContext items={visibleForSelectedTab.map((f) => f.id)} strategy={verticalListSortingStrategy}>
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ mb: 1 }}>
                <Tooltip title="Add field">
                  <IconButton
                    size="small"
                    color={accentColor}
                    onClick={() => handleAdd(selectedTab)}
                    disabled={!canAdd}
                    aria-label={`Add ${selectedTab} field`}
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
              <TransactionFieldTable
                visibleFields={visibleByType[selectedTab]}
                inUseSet={inUseSet}
                usageLoading={usageLoading}
                saving={saving}
                onEdit={handleEdit}
                onHide={handleHide}
                onDeleteClick={handleDeleteClick}
                isMobile={isMobile}
                transactionType={selectedTab}
                addButtonInHeader
                readOnly={readOnly}
              />
              <Box ref={(el: HTMLDivElement | null) => { hiddenAccordionRefs.current[selectedTab] = el; }} sx={{ mt: 1.5 }}>
                <Paper
                  variant="outlined"
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  <Accordion
                    expanded={hiddenAccordionExpandedByType[selectedTab]}
                    onChange={(_, expanded) =>
                      setHiddenAccordionExpandedByType((s) => ({ ...s, [selectedTab]: expanded }))
                    }
                    sx={{
                      "& .MuiAccordionSummary-root": { minHeight: 40, py: 0.25 },
                      "&:before": { display: "none" },
                      boxShadow: "none",
                      border: "none",
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" color="action" />}>
                      <Typography variant="body2" fontWeight="500" color="text.secondary">
                        Hidden fields ({selectedTab}) {hiddenByType[selectedTab].length > 0 && `(${hiddenByType[selectedTab].length})`}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0.5, py: 0.5, pt: 0 }}>
                      {hiddenByType[selectedTab].length === 0 ? (
                        <Paper
                          elevation={0}
                          variant="outlined"
                          sx={{
                            p: 2,
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 1,
                            textAlign: "center",
                          }}
                        >
                          <Stack spacing={0.5} alignItems="center">
                            <VisibilityOffIcon sx={{ fontSize: 36, color: "action.disabled" }} />
                            <Typography variant="body2" color="text.secondary">
                              No hidden {selectedTab} fields
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 320 }}>
                              Hidden fields are moved here. Unhide to show them again in the form.
                            </Typography>
                          </Stack>
                        </Paper>
                      ) : (
                        isMobile ? (
                          <Stack spacing={0.5}>
                            {hiddenByType[selectedTab].map((field) => (
                              <HiddenTransactionFieldRow
                                key={field.id}
                                field={field}
                                forList={selectedTab.charAt(0).toUpperCase() + selectedTab.slice(1)}
                                inUse={inUseSet.has(field.id)}
                                usageLoading={usageLoading}
                                saving={saving}
                                onEdit={handleEdit}
                                onUnhide={handleUnhide}
                                onDeleteClick={handleDeleteClick}
                                isMobile={true}
                                readOnly={readOnly}
                              />
                            ))}
                          </Stack>
                        ) : (
                          <TableContainer
                            component={Paper}
                            elevation={0}
                            variant="outlined"
                            sx={{
                              border: 1,
                              borderColor: "divider",
                              borderRadius: 1,
                              overflowX: "auto",
                              bgcolor: "background.paper",
                              width: "100%",
                            }}
                          >
                            <Table sx={{ tableLayout: "fixed", width: "100%", minWidth: 400 }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell align="left" sx={{ ...TRANSACTION_FIELD_TABLE_CELL_SX.order, fontWeight: 600, color: "text.secondary" }}>Order</TableCell>
                                  <TableCell align="left" sx={{ ...TRANSACTION_FIELD_TABLE_CELL_SX.fieldName, fontWeight: 600, color: "text.secondary" }}>Field name</TableCell>
                                  <TableCell align="left" sx={{ ...TRANSACTION_FIELD_TABLE_CELL_SX.type, fontWeight: 600, color: "text.secondary" }}>Type</TableCell>
                                  <TableCell align="left" sx={{ ...TRANSACTION_FIELD_TABLE_CELL_SX.for, fontWeight: 600, color: "text.secondary" }}>For</TableCell>
                                  <TableCell align="right" sx={{ ...TRANSACTION_FIELD_TABLE_CELL_SX.actions, fontWeight: 600, color: "text.secondary" }}>Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {hiddenByType[selectedTab].map((field) => (
                                  <HiddenTransactionFieldRow
                                    key={field.id}
                                    field={field}
                                    forList={selectedTab.charAt(0).toUpperCase() + selectedTab.slice(1)}
                                    inUse={inUseSet.has(field.id)}
                                    usageLoading={usageLoading}
                                    saving={saving}
                                    onEdit={handleEdit}
                                    onUnhide={handleUnhide}
                                    onDeleteClick={handleDeleteClick}
                                    isMobile={false}
                                    readOnly={readOnly}
                                  />
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Paper>
              </Box>
            </Box>
          </SortableContext>
          <DragOverlay dropAnimation={null}>
            {activeField ? (
              <Paper
                variant="outlined"
                sx={{
                  px: 2,
                  py: 1.5,
                  boxShadow: 3,
                  cursor: "grabbing",
                  minWidth: 280,
                  bgcolor: "background.paper",
                  borderColor: "divider",
                }}
              >
                <Stack direction="row" alignItems="flex-start" gap={1} flexWrap="wrap">
                  <DragIndicatorIcon sx={{ color: "action.active", mt: 0.5 }} fontSize="small" />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                      <TransactionFieldIcon icon={activeField.icon} fontSize="medium" sx={{ color: "action.active" }} />
                      <Typography fontWeight={500} color="text.primary">{activeField.label}</Typography>
                      <Chip
                        label={activeField.type === "attachment" ? "Attachment" : "Text"}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: "divider", color: "text.secondary" }}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      For: {getFieldTransactionType(activeField).charAt(0).toUpperCase() + getFieldTransactionType(activeField).slice(1)}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            ) : null}
          </DragOverlay>
        </DndContext>
      </Box>

      <TransactionFieldDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onConfirm={handleDialogConfirm}
        editingField={editingField}
        saving={saving}
        existingFields={fields}
        initialTransactionType={addForTransactionType ?? undefined}
      />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setFieldToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        itemName={fieldToDelete?.label ?? ""}
        itemType="transaction field"
        title="Delete transaction field"
        confirmButtonText="Delete"
        disabled={saving}
        disableClose={saving}
      />
    </Box>
  );
}
